import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { AuthProvider } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { ProtectedRoute, PublicRoute } from "@/components/auth/ProtectedRoute";
import Dashboard from "@/pages/Dashboard";
import Gardens from "@/pages/Gardens";
import Controllers from "@/pages/Controllers";
import Sensors from "@/pages/Sensors";
import Schedules from "@/pages/Schedules";
import Triggers from "@/pages/Triggers";
import Calendar from "@/pages/Calendar";
import Weather from "@/pages/Weather";
import Analytics from "@/pages/Analytics";
import Alarms from "@/pages/Alarms";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";
import LandingOrRedirect from "@/components/landing/LandingOrRedirect";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      retry: (failureCount, error: any) => {
        // No reintentar si es error de autenticación
        if (error?.error?.code === 'UNAUTHORIZED' || 
            error?.message?.includes('401') ||
            error?.message?.includes('Session expired')) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <WebSocketProvider>
        <TooltipProvider>
          <BrowserRouter>
            <Routes>
              {/* Ruta pública - Landing (redirige a /dashboard si ya está autenticado) */}
              <Route path="/" element={<LandingOrRedirect />} />
              {/* Ruta pública - Login */}
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } 
              />
              
              {/* Rutas protegidas */}
              <Route 
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/alarms" element={<Alarms />} />
                <Route path="/gardens" element={<Gardens />} />
                <Route path="/controllers" element={<Controllers />} />
                <Route path="/sensors" element={<Sensors />} />
                <Route path="/schedules" element={<Schedules />} />
                <Route path="/triggers" element={<Triggers />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/weather" element={<Weather />} />
                <Route path="/analytics" element={<Analytics />} />
                
                {/* Rutas que requieren superadmin */}
                <Route 
                  path="/companies" 
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <Companies />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users" 
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <Users />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
              </Route>
              
              {/* Página 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster />
            <Sonner />
          </BrowserRouter>
        </TooltipProvider>
      </WebSocketProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
